import type { WorkspaceSharing } from '@/features/workspace/workspace-access.js';

import type { ConnectorConnectionScope } from '../../services/integration-api.js';

/** The lanes these labels cover — every value of the union except the unknown one. */
type KnownLane = Exclude<ConnectorConnectionScope, null>;

/**
 * WHERE a connection lives, wherever one is shown beside its name — the connectors grid, the SharePoint
 * knowledge picker and the transfer screen.
 *
 * These labels used to answer who may USE the connection, and that question died: everybody connects
 * under their own account, so every live connection is the caller's own and "Only for you" said the same
 * thing on every card. What still differs between two of them is where each one LIVES. One was made in
 * this workspace and stays here; the other lives in the caller's personal space and travels with them
 * into every workspace they belong to, which is also where it has to be managed.
 *
 * So only the travelling one is marked. The workspace lane says nothing on purpose — an unmarked
 * connection is one that lives here, which is the reading a person already has when they are looking at
 * a workspace's own connectors, and a label on every card would push the mark back into noise.
 *
 * `service` is a credential belonging to the workspace rather than to any person, and identity no longer
 * hands one to a caller at all — only unattended runs resolve it. The mark stays because the two images
 * roll separately: an identity still on the previous build can send such a row among the caller's
 * connections, and in that window the mark is the only thing telling somebody they are acting as the
 * workspace rather than as themselves. It says whose the credential is and what to do about it.
 */
const ORIGIN_LABELS: Record<KnownLane, string | null> = {
  workspace: null,
  personal: 'Personal space',
  service: 'Workspace credential',
};

/**
 * What the label costs the reader, in full, for the hover title beside it. Two words cannot carry the
 * difference between a connection that stays put and one that travels, so the detail lives here — one
 * place with the labels, because the two surfaces wording it differently is how a person ends up
 * believing the wrong one.
 */
const ORIGIN_HINTS: Record<KnownLane, string | null> = {
  workspace: null,
  personal:
    'This connection lives in your personal space and comes with you into every workspace you belong to — manage it there. Other members never see it in their own list, and a link that names it tells them so instead of opening your files.',
  service:
    'A credential that belongs to this workspace rather than to you. Unattended runs go through it, and nothing new can be made — connect your own account and yours is used instead.',
};

/**
 * The same fact said INSIDE the status pill, as the tail of a sentence that starts with "Connected".
 *
 * The pill is where a person looks to learn the state of a connector, so the one thing that qualifies
 * that state belongs in it rather than beside it — a separate chip under the name read as a third fact
 * competing with the connection's own name. Two texts for one fact live here together on purpose: the
 * short label above is a standalone badge in a list of rows, where "as personal" would be a fragment,
 * and this one is a phrase, where "Personal space" would not read as English after "Connected".
 * Keeping them apart in two files is how an interface ends up saying two different things.
 */
const ORIGIN_PILL_SUFFIX: Record<KnownLane, string | null> = {
  workspace: null,
  personal: 'as personal',
  service: 'as a workspace credential',
};

/**
 * Whether a lane has something to say in the workspace on screen.
 *
 * In the caller's OWN personal space the person-owned lanes all describe the same place, so marking
 * them answers a question nobody asked and the same mark on every row carries no information.
 *
 * The service lane is exempt, because it is not one of those: it says the credential is NOT the
 * caller's. This rule used to cover it too, on the premise that a personal workspace holds no
 * `__workspace__` row at all — and that premise was wrong. A dev workspace with `kind = 'personal'`
 * held a service-lane GitLab credential, and with the mark suppressed the card read a bare
 * "Connected" while no chat turn could use it. The exemption costs nothing where the lane does not
 * occur: a workspace with no such row has no card standing on one.
 *
 * A shared workspace is where the lanes actually sit side by side, and `'unknown'` reads as shared: the
 * workspace row is a separate read that lags the grid, and withholding the mark until it lands would
 * hide the one thing the mark exists to say.
 */
function marksLane(lane: KnownLane, sharing: WorkspaceSharing): boolean {
  return lane === 'service' || sharing !== 'personal';
}

/**
 * The origin label for a connection, or `null` when there is nothing to say — a lane this build does not
 * know, the workspace's own lane, or a person-owned lane in a personal workspace, where they all
 * describe the same place. A workspace credential is named wherever it sits.
 */
export function connectionOriginLabel(
  scope: ConnectorConnectionScope,
  sharing: WorkspaceSharing,
): string | null {
  if (scope === null || !marksLane(scope, sharing)) return null;
  return ORIGIN_LABELS[scope];
}

/**
 * What the status pill adds after "Connected", or `null` when the lane says nothing — the pill then
 * reads "Connected" alone, which is what a connection living here has always said.
 */
export function connectionOriginPillSuffix(
  scope: ConnectorConnectionScope,
  sharing: WorkspaceSharing,
): string | null {
  if (scope === null || !marksLane(scope, sharing)) return null;
  return ORIGIN_PILL_SUFFIX[scope];
}

/**
 * The sentences behind the label, for the `title` on whichever element carries it. `null` wherever the
 * label itself is `null`, so a surface with no label has no hint either.
 */
export function connectionOriginHint(
  scope: ConnectorConnectionScope,
  sharing: WorkspaceSharing,
): string | null {
  if (scope === null || !marksLane(scope, sharing)) return null;
  return ORIGIN_HINTS[scope];
}
